/**
 * SaveAndLoad - handles game save and load functionality
 */

import { $dom } from '../utils/DOM';
import { Finder } from '../utils/Finder';
import { Globals } from '../core/Globals';
import { runEventScripts } from '../core/ScriptRunner';
import { Settings } from '../core/Settings';
import { gameController } from '../core/GameController';
import { DateTimes } from '../utils/DateTimes';
import { FileSystemSaveStorage } from '../utils/FileSystemStorage';
import { REGALIA_PLAYER_VERSION } from '../utils/Versioning';
import { waitAsync } from '../utils/Utilities';

declare let TheGame: any;
declare let OriginalGame: any;

// Forward declarations
declare const DeepDiff: any;
declare const LZMA: any;
declare const parseNrbf: any;
declare const moment: any;
declare namespace GameUI {
    function setDarkMode(enabled: boolean): void;
}
declare function SetupCustomPropertyData(data: any[]): any;
declare function SetupExitData(data: any[]): any;
declare function GameCloneForDiff(game: any): any;
declare function resetJankyJavascriptExecutor(): void;

export interface SaveGame {
    id: number;
    name: string;
    date: string;
}

export interface SaveData {
    id: number;
    name: string;
    date: string;
    gameData: any;
    cheatFreezes?: any;
    zMainText?: string;
    currentImage?: string;
    gameTitle?: string;
    gameVersion?: string;
    regaliaVersion?: string;
}

function sanitizeMainTextHtml(rawHtml: string): string {
    const container = document.createElement('div');
    container.innerHTML = rawHtml;
    container.querySelectorAll('.ps__rail-x, .ps__rail-y, .ps__thumb-x, .ps__thumb-y').forEach((el) => {
        el.remove();
    });
    return container.innerHTML;
}

export const SavedGames = {
    titleForSave: function (): string {
        return gameController.title();
    },

    keyForIndex: function (): string {
        return this.titleForSave() + '-Saves';
    },

    keyForSave: function (n: number): string {
        return this.titleForSave() + '-Save' + n;
    },

    getIndex: async function (): Promise<{ [key: string]: SaveGame }> {
        const gameTitle = this.titleForSave();
        
        // Migrate from localStorage if File System API is available
        try {
            const available = await FileSystemSaveStorage.isAvailable();
            if (available) {
                await FileSystemSaveStorage.migrateFromLocalStorage(gameTitle);
            }
        } catch (e) {
            console.error('Migration from localStorage failed:', e);
        }
        
        const index = await FileSystemSaveStorage.getIndex(gameTitle);
        if (index) {
            return index;
        } else {
            await this.reset();
            return {};
        }
    },

    getSortedSaves: async function (field?: number, ascending?: boolean): Promise<SaveGame[]> {
        if (field === undefined) field = 0;
        const asc = !!ascending;

        const parsedIndex = await this.getIndex();
        return Object.keys(parsedIndex).sort((a, b) => {
            let diff: number;
            switch (field) {
                default:
                case 0:  // By index
                    diff = parseInt(a, 10) - parseInt(b, 10);
                    break;
                case 1:  // By name
                    const nameA = parsedIndex[a].name.toLocaleLowerCase();
                    const nameB = parsedIndex[b].name.toLocaleLowerCase();
                    diff = nameA.localeCompare(nameB);
                    break;
                case 2:  // By date
                    const dateA = new Date(parsedIndex[a].date);
                    const dateB = new Date(parsedIndex[b].date);
                    diff = dateA.getTime() - dateB.getTime();
                    break;
            }
            return asc ? diff : -diff;
        }).map((id) => {
            return parsedIndex[id];
        });
    },

    getSave: async function (id: number): Promise<SaveData | null> {
        const gameTitle = this.titleForSave();
        return await FileSystemSaveStorage.getSave(gameTitle, id);
    },

    nextSaveId: async function (): Promise<number> {
        const sortedSaves = await this.getSortedSaves();
        const lastSave = sortedSaves[0];
        return (lastSave ? lastSave.id : 0) + 1;
    },

    createSave: async function (id: number, name: string, date: string, gameData: any): Promise<void> {
        const gameTitle = this.titleForSave();
        const savedGames = await this.getIndex();
        savedGames[id] = {
            id: id,
            name: name,
            date: date
        };

        await runEventScripts('<<Before Game Save>>');

        let mainText = String($dom("#MainText").html());
        mainText = sanitizeMainTextHtml(mainText);

        // Compress the main text
        let zData = (window as any).LZMA?.compress?.(mainText, 1);
        let zText = '';
        if (zData && zData.length & 1) zData.push(0);  // Pad with 0 to get a multiple of 2 bytes
        if (zData) {
            zData = new Int8Array(zData);
            // Convert the result to an UTF-16 string (2 bytes per characters)
            zText = String.fromCharCode.apply(null, Array.from(new Uint16Array(zData.buffer)));
        } else {
            console.warn(`Compression failed for save ${id}, storing without compressed text`);
        }

        const saveData = {
            id: id,
            name: name,
            date: date,
            gameData: gameData,
            cheatFreezes: (window as any).cheatFreezes,
            zMainText: zText,
            currentImage: (window as any).Globals?.currentImage,
            gameTitle: (window as any).TheGame?.Title || gameTitle,
            gameVersion: (window as any).TheGame?.GameVersion || '',
            regaliaVersion: REGALIA_PLAYER_VERSION,
        };

        await FileSystemSaveStorage.setSave(gameTitle, id, saveData);
        await FileSystemSaveStorage.setIndex(gameTitle, savedGames);
    },

    destroySave: async function (id: number): Promise<void> {
        const gameTitle = this.titleForSave();
        const savedGames = await this.getIndex();
        delete savedGames[id];
        await FileSystemSaveStorage.deleteSave(gameTitle, id);
        await FileSystemSaveStorage.setIndex(gameTitle, savedGames);
    },

    reset: async function (): Promise<void> {
        const gameTitle = this.titleForSave();
        await FileSystemSaveStorage.clear(gameTitle);
        await FileSystemSaveStorage.setIndex(gameTitle, {});
    },

    import: async function (newSaves: SaveData[]): Promise<void> {
        const gameTitle = this.titleForSave();
        const savedGames = await this.getIndex();
        for (let i = 0; i < newSaves.length; i++) {
            const newSave = newSaves[i];
            savedGames[newSave.id] = {
                id: newSave.id,
                name: newSave.name,
                date: newSave.date
            };
            await FileSystemSaveStorage.setSave(gameTitle, newSave.id, newSave);
        }
        await FileSystemSaveStorage.setIndex(gameTitle, savedGames);
    },

    saveDataFor: function (game: any): any {
        return (window as any).DeepDiff?.diff?.(OriginalGame, game, {
            prefilter: (path: any[], key: string) => {
                // Ignore properties that cannot change
                switch (key) {
                    case 'PassCommands':
                    case 'FailCommands':
                    case 'Conditions':
                    case 'EnhInputData':
                    case 'cloneForDiff':  // function, do not compare
                    case '_owner':
                        return true;
                }
                return false;
            }
        });
    },

    applySaveToGame: async function (game: any, savedGame: SaveData | null): Promise<void> {
        if (!savedGame) {
            console.warn('No save game data to apply');
            return;
        }
        let changes = savedGame.gameData;
        // Check for old save where changes were stringified for no good reason
        if (typeof changes == 'string') changes = JSON.parse(savedGame.gameData);
        const orderedChanges = (window as any).orderChanges?.(changes) || changes;

        for (let i = 0; i < orderedChanges.length; i++) {
            (window as any).DeepDiff?.applyChange?.(TheGame, true, orderedChanges[i]);
        }

        if (savedGame.zMainText !== undefined) {
            const zData = new Uint16Array(savedGame.zMainText.length);
            for (let i = 0; i < zData.length; ++i) {
                zData[i] = savedGame.zMainText.charCodeAt(i);
            }
            const mainText = (window as any).LZMA?.decompress?.(new Int8Array(zData.buffer));
            if (mainText) {
                const sanitizedText = sanitizeMainTextHtml(mainText);
                $dom("#MainText").html(sanitizedText);
                (window as any).GameUI?.setDarkMode?.((Settings as any).darkMode);
                setTimeout(() => {
                    const mainTextEl = document.getElementById('MainText');
                    if (mainTextEl && (window as any).mainTextScrollbar) {
                        (window as any).mainTextScrollbar.update();
                        const scrollTop = mainTextEl.scrollTop;
                        const maxScrollTop = mainTextEl.scrollHeight - mainTextEl.clientHeight;
                        if (scrollTop > maxScrollTop && maxScrollTop >= 0) {
                            mainTextEl.scrollTop = maxScrollTop;
                        }
                        (window as any).mainTextScrollbar.update();
                    }
                }, 0);
            }
        }

        // Reset JS executor to allow a fresh evaluation context after load.
        if (typeof resetJankyJavascriptExecutor === 'function') {
            resetJankyJavascriptExecutor();
        }
    },

    importRSV: async function (buffer: ArrayBuffer): Promise<any> {
        // openssl enc -aes-256-cbc -nosalt -d -in rags_save.rsv -K 'B4BDC259B1104A6531F8109C851BCF9AD09BDD208851C9CBAB782AEC356CC1E3' -iv '31F8109C851BCF9A203D6C71A7BD1487'
        const aes_key = new Uint8Array([
            0xB4, 0xBD, 0xC2, 0x59, 0xB1, 0x10, 0x4A, 0x65,
            0x31, 0xF8, 0x10, 0x9C, 0x85, 0x1B, 0xCF, 0x9A,
            0xD0, 0x9B, 0xDD, 0x20, 0x88, 0x51, 0xC9, 0xCB,
            0xAB, 0x78, 0x2A, 0xEC, 0x35, 0x6C, 0xC1, 0xE3]);
        const aes_iv = new Uint8Array([
            0x31, 0xF8, 0x10, 0x9C, 0x85, 0x1B, 0xCF, 0x9A,
            0x20, 0x3D, 0x6C, 0x71, 0xA7, 0xBD, 0x14, 0x87]);

        let data: ArrayBuffer | undefined;
        try {
            console.log(`Decrypting file content...`);
            const key = await window.crypto.subtle.importKey("raw", aes_key, "AES-CBC", false, ["decrypt"]);
            data = await window.crypto.subtle.decrypt({ name: "AES-CBC", iv: aes_iv }, key, buffer);
        } finally {
            // Free memory
            if (data === undefined) alert(`Decryption failed!`);
        }

        let root: any;
        try {
            console.log(`Extracting data...`);
            $dom('.import-menu-status').html('Extracting RSV file...');
            // Let the HTML update
            await waitAsync();
            let percent = -1;
            root = await (window as any).parseNrbf?.(data, async (pos: number, size: number, step: number) => {
                if (step > 0) --step;
                const new_percent = Math.floor(pos * 90 / size + step * 5);
                if (new_percent != percent) {
                    percent = new_percent;
                    $dom('.import-menu-status').text(`Extracting RSV file (${percent}%)`);
                    // Let the HTML update
                    await waitAsync();
                }
            });
        } finally {
            // Free memory
            if (root === undefined) (window as any).showAlert?.("Error", "Extraction failed!");
        }

        console.debug(root);
        return root;
    },

    applyRsvToGame: function (_game: any, root: any): void {
        // FIXME We should only change "_game" but we keep touching the global
        //       variable "TheGame" instead (because we use the Finder)
        console.log(`Importing data...`);

        const VarType_map: { [key: number]: string } = {
            0: "VT_UNINITIALIZED",
            1: "VT_NUMBER",
            2: "VT_STRING",
            3: "VT_DATETIME",
            4: "VT_NUMBERARRAY",
            5: "VT_STRINGARRAY",
            6: "VT_DATETIMEARRAY",
        };

        const Direction_map: { [key: number]: string } = {
            0: "Empty",
            1: "North",
            2: "South",
            3: "East",
            4: "West",
            5: "Up",
            6: "Down",
            7: "NorthEast",
            8: "NorthWest",
            9: "SouthWest",
            10: "SouthEast",
            11: "In",
            12: "Out",
        };

        const CharGender_map: { [key: number]: string } = {
            0: "Male",
            1: "Female",
            2: "Other",
        };

        const TimerType_map: { [key: number]: string } = {
            0: "TT_RUNALWAYS",
            1: "TT_LENGTH",
        };

        const LocationType_map: { [key: number]: string } = {
            0: "LT_NULL",
            1: "LT_IN_OBJECT",
            2: "LT_ON_OBJECT",
            3: "LT_ROOM",
            4: "LT_PLAYER",
            5: "LT_CHARACTER",
            6: "LT_PORTAL",
        };

        const ActionInputType_map: { [key: number]: string } = {
            0: "None",
            1: "Object",
            2: "Character",
            3: "ObjectOrCharacter",
            4: "Text",
            5: "Custom",
            6: "Inventory",
        };

        const CheckType_map: { [key: number]: string } = {
            0: "CT_Uninitialized",
            1: "And",
            2: "Or",
        };

        function enumValue(_enum: any): number {
            return (typeof (_enum) == 'object' && 'value__' in _enum)
                ? _enum.value__ : _enum;
        }

        function VarType(vartype: any): string {
            return VarType_map[enumValue(vartype)] || VarType_map[0];
        }

        function Direction(direction: any): string {
            return Direction_map[enumValue(direction)] || Direction_map[0];
        }

        function CharGender(gender: any): string {
            return CharGender_map[enumValue(gender)] || CharGender_map[0];
        }

        function TimerType(ttype: any): string {
            return TimerType_map[enumValue(ttype)] || TimerType_map[0];
        }

        function LocationType(location: any): string {
            return LocationType_map[enumValue(location)] || LocationType_map[0];
        }

        function ActionInputType(aitype: any): string {
            return ActionInputType_map[enumValue(aitype)] || ActionInputType_map[0];
        }

        function CheckType(ctype: any): string {
            return CheckType_map[enumValue(ctype)] || CheckType_map[0];
        }

        function Guid(guid: any): string {
            // "n >>> 0" convert the number to unsigned
            function hex2(n: number): string {
                return (n >>> 0).toString(16).padStart(2, '0').slice(-2);
            }
            function hex4(n: number): string {
                return (n >>> 0).toString(16).padStart(4, '0').slice(-4);
            }
            function hex8(n: number): string {
                return (n >>> 0).toString(16).padStart(8, '0').slice(-8);
            }

            return hex8(guid._a) + '-'
                + hex4(guid._b) + '-' + hex4(guid._c) + '-'
                + hex2(guid._d) + hex2(guid._e) + '-'
                + hex2(guid._f) + hex2(guid._g) + hex2(guid._h)
                + hex2(guid._i) + hex2(guid._j) + hex2(guid._k);
        }

        function DateTime(dt: any): string {
            // Convert from DateTime representation
            const m = (window as any).moment?.('01/01/0001 00:00:00', "DD/MM/YYYY hh:mm:ss");
            const seconds = dt.ticks / 10000000n;
            const nSeconds = Number(seconds);
            if (seconds != BigInt(nSeconds)) {
                console.debug(`Precision lost: ${seconds} => ${nSeconds}`);
            }
            if (m) {
                m.add(nSeconds, 'seconds');
                // FIXME The date it always off by 3039 seconds plus the DST offset
                return m.format(DateTimes.defaultDateFormat);
            }
            return '';
        }

        function adaptText(str: string): string {
            return str.replace(/\n/g, '<br>').replace(/\r/g, '');
        }

        function adaptText_nobr(str: string): string {
            return str.replace(/\r/g, '');
        }

        let success = true;

        const refGame = JSON.parse(JSON.stringify(_game));

        /** This function is just an helper to make sure there is no typo in RSV
         * conversion code.
         */
        function update(gameObject: any, gameField: string, object: any, objectField?: string, convertFunc?: (val: any, obj?: any, objField?: string, gameObj?: any, gameField?: string) => any): void {
            if (objectField === undefined || objectField.length == 0) {
                objectField = gameField;
            }

            if (!(gameField in gameObject)) {
                console.warn(gameObject, gameField, object, objectField);
                throw new Error(`${gameField} not in game object ${gameObject}`);
            }
            if (!(objectField in object)) {
                console.warn(gameObject, gameField, object, objectField);
                throw new Error(`${objectField} not in save object ${object}`);
            }

            if (convertFunc !== undefined) {
                gameObject[gameField] = convertFunc(object[objectField], object, objectField, gameObject, gameField);
            } else {
                gameObject[gameField] = object[objectField];
            }
        }

        function updateAction(gameAction: any, action: any): void {
            update(gameAction, 'bActive', action);
            update(gameAction, 'bConditionFailOnFirst', action);
            update(gameAction, 'overridename', action, 'nameoverride');
            update(gameAction, 'actionparent', action);
            update(gameAction, 'InputType', action, '', ActionInputType);
            update(gameAction, 'CustomChoices', action);  // array of strings
        }

        if (root.CharacterList) {
            root.CharacterList.forEach((saveObj: any) => {
                const gameObj = Finder.character(saveObj.Charname);
                if (!gameObj) {
                    console.error("Cannot find character " + saveObj.Charname);
                    success = false;
                    return;
                }

                gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                    return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
                });

                if (saveObj.Actions) {
                    saveObj.Actions.forEach((action: any) => {
                        const gameAction = Finder.action(gameObj.Actions, action.name);
                        if (!gameAction) {
                            console.error("Cannot find action " + action.name + " for character " + saveObj.Charname);
                            success = false;
                            return;
                        }

                        updateAction(gameAction, action);
                    });
                }

                update(gameObj, 'CharGender', saveObj, '', CharGender);
                update(gameObj, 'CharPortrait', saveObj);
                update(gameObj, 'CharnameOverride', saveObj);
                update(gameObj, 'CurrentRoom', saveObj);
                update(gameObj, 'Description', saveObj, '', adaptText);
                update(gameObj, 'bAllowInventoryInteraction', saveObj);
                update(gameObj, 'bEnterFirstTime', saveObj);
                update(gameObj, 'bLeaveFirstTime', saveObj);
            });
        }

        if (root.ObjectList) {
            root.ObjectList.forEach((saveObj: any) => {
                const uid = Guid(saveObj.UniqueIdentifier);
                const gameObj = Finder.object(uid);
                if (!gameObj) {
                    console.error("Cannot find object " + uid + " (" + saveObj.name + ")");
                    success = false;
                    return;
                }

                gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                    return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
                });

                if (saveObj.Actions) {
                    saveObj.Actions.forEach((action: any) => {
                        const gameAction = Finder.action(gameObj.Actions, action.name);
                        if (!gameAction) {
                            console.error("Cannot find action " + action.name + " for object " + saveObj.name);
                            success = false;
                            return;
                        }

                        updateAction(gameAction, action);
                    });
                }

                update(gameObj, 'bCarryable', saveObj);
                update(gameObj, 'bContainer', saveObj);
                update(gameObj, 'bEnterFirstTime', saveObj);
                update(gameObj, 'bEnterable', saveObj);
                if ('bImportant' in saveObj) update(gameObj, 'bImportant', saveObj);
                update(gameObj, 'bLeaveFirstTime', saveObj);
                update(gameObj, 'bLockable', saveObj);
                update(gameObj, 'bLocked', saveObj);
                update(gameObj, 'bOpen', saveObj);
                update(gameObj, 'bOpenable', saveObj);
                update(gameObj, 'bReadable', saveObj);
                update(gameObj, 'bVisible', saveObj);
                update(gameObj, 'bWearable', saveObj);
                update(gameObj, 'bWorn', saveObj);
                update(gameObj, 'dWeight', saveObj);
                update(gameObj, 'description', saveObj, '', adaptText);
                update(gameObj, 'locationname', saveObj);
                update(gameObj, 'locationtype', saveObj, '', LocationType);
                update(gameObj, 'name', saveObj, '', adaptText);
                update(gameObj, 'preposition', saveObj);
                update(gameObj, 'sdesc', saveObj, '', adaptText);
            });
        }

        if (root.RoomList) {
            root.RoomList.forEach((saveObj: any) => {
                const uid = Guid(saveObj.UniqueID);
                const gameObj = Finder.room(uid);
                if (!gameObj) {
                    console.error("Cannot find room " + uid + " (" + saveObj.Name + ")");
                    success = false;
                    return;
                }

                gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                    return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
                });

                if (saveObj.Actions) {
                    saveObj.Actions.forEach((action: any) => {
                        const gameAction = Finder.action(gameObj.Actions, action.name);
                        if (!gameAction) {
                            console.error("Cannot find action " + action.name + " for room " + saveObj.Name);
                            success = false;
                            return;
                        }

                        updateAction(gameAction, action);
                    });
                }

                update(gameObj, 'Description', saveObj, '', adaptText);
                update(gameObj, 'Name', saveObj, '', adaptText);
                update(gameObj, 'RoomPic', saveObj);
                update(gameObj, 'SDesc', saveObj, '', adaptText);
                update(gameObj, 'bEnterFirstTime', saveObj);
                update(gameObj, 'bLeaveFirstTime', saveObj);

                gameObj.Exits = [];
                if (saveObj.Exits) {
                    saveObj.Exits.forEach((exit: any) => {
                        gameObj.Exits.push((window as any).SetupExitData?.([
                            Direction(exit.Direction),
                            exit.bActive,
                            exit.DestinationRoom,
                            exit.PortalObjectName]));
                    });
                }
            });
        }

        if (root.TimerList) {
            root.TimerList.forEach((saveObj: any) => {
                const gameObj = Finder.timer(saveObj.Name);
                if (!gameObj) {
                    console.error("Cannot find timer " + saveObj.Name);
                    success = false;
                    return;
                }

                gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                    return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
                });

                if (saveObj.Actions) {
                    saveObj.Actions.forEach((action: any) => {
                        const gameAction = Finder.action(gameObj.Actions, action.name);
                        if (!gameAction) {
                            console.error("Cannot find action " + action.name + " for timer " + saveObj.Name);
                            success = false;
                            return;
                        }

                        updateAction(gameAction, action);
                    });
                }

                update(gameObj, 'Active', saveObj);
                update(gameObj, 'LiveTimer', saveObj);
                update(gameObj, 'Restart', saveObj);
                update(gameObj, 'TType', saveObj, '', TimerType);
                update(gameObj, 'TimerSeconds', saveObj);
                update(gameObj, 'TurnNumber', saveObj);
                if ('curtickcount' in saveObj) {
                    gameObj.curtickcount = saveObj.curtickcount;
                } else {
                    gameObj.curtickcount = 0;
                }
            });
        }

        if (root.VariableList) {
            root.VariableList.forEach((saveObj: any) => {
                const gameObj = Finder.variable(saveObj.varname);
                if (!gameObj) {
                    console.error("Cannot find variable " + saveObj.varname);
                    success = false;
                    return;
                }

                gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                    return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
                });

                if (gameObj.vartype.endsWith("ARRAY")) {
                    const stringArray = gameObj.vartype == "VT_STRINGARRAY";
                    const numberArray = gameObj.vartype == "VT_NUMBERARRAY";
                    gameObj.VarArray = saveObj.VarArray.map((el: any) => {
                        if (Array.isArray(el)) {
                            if (stringArray) return el.map(adaptText_nobr);
                            if (numberArray) return el.map(Number);
                            return el.map(DateTime);
                        } else {
                            if (stringArray) return adaptText(el);
                            if (numberArray) return Number(el);
                            return DateTime(el);
                        }
                    });
                }
                update(gameObj, 'dNumType', saveObj);
                update(gameObj, 'sString', saveObj, '', adaptText);
                if (gameObj.vartype == "VT_DATETIME") {
                    // Only update if the variable should contain a DateTime as conversion is not
                    // accurate so it creates too much deltas with the original game data
                    update(gameObj, 'dtDateTime', saveObj, '', DateTime);
                }
            });
        }

        if (root.StatusBarList) {
            root.StatusBarList.forEach((saveObj: any) => {
                if (!TheGame) return;
                const gameObj = TheGame.StatusBarItems.find((item: any) => {
                    return item.Name == saveObj.Name;
                });
                if (!gameObj) {
                    console.error("Cannot find status bar item " + saveObj.Name);
                    success = false;
                    return;
                }

                update(gameObj, 'bVisible', saveObj);
            });
        }

        if (root.ThePlayer) {
            const saveObj = root.ThePlayer;
            if (!TheGame) return;
            const gameObj = TheGame.Player;

            gameObj.CustomProperties = saveObj.CustomProperties.map((prop: any) => {
                return (window as any).SetupCustomPropertyData?.([adaptText(prop.Name), adaptText(prop.Value)]);
            });

            if (saveObj.Actions) {
                saveObj.Actions.forEach((action: any) => {
                    const gameAction = Finder.action(gameObj.Actions, action.name);
                    if (!gameAction) {
                        console.error("Cannot find action " + action.name + " for player");
                        success = false;
                        return;
                    }

                    updateAction(gameAction, action);
                });
            }

            gameObj.CurrentRoom = saveObj.CurrentRoom ? Guid(saveObj.CurrentRoom.UniqueID) : null;
            update(gameObj, 'Description', saveObj, '', adaptText);
            update(gameObj, 'Name', saveObj);
            update(gameObj, 'PlayerGender', saveObj, '', CharGender);
            update(gameObj, 'PlayerPortrait', saveObj);
        }

        // Re-create the name/uid maps
        Finder.addMaps(TheGame!);

        if (!success) {
            (window as any).showAlert?.("Import Error", "Import failed. Check console for details.");
            throw new Error("Import failed.");
        }

        console.log(`Done.`);
        if (typeof resetJankyJavascriptExecutor === 'function') {
            resetJankyJavascriptExecutor();
        }
    },

    renameSave: async function (id: number, name: string): Promise<void> {
        const gameTitle = this.titleForSave();
        const savedGames = await this.getIndex();
        savedGames[id].name = name;
        await FileSystemSaveStorage.setIndex(gameTitle, savedGames);

        // No need to update the name in the full save data as it is never used
        // and it takes a significant time
    },
};

/**
 * Persist key-value pair to localStorage with error handling
 */
export function persistKeyValue(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch (e: any) {
        if (e instanceof DOMException && e.message.match(/exceeded the quota/)) {
            (window as any).showAlert?.("Save Error", "Save operation failed: localStorage quota exceeded.\n\nDelete some saves and try again.");
        }
        throw e;
    }
}

/**
 * Check if two paths are equal
 */
export function pathsEqual(p1: any[], p2: any[]): boolean {
    return JSON.stringify(p1) == JSON.stringify(p2);
}

/**
 * Order changes for proper application
 */
export function orderChanges(changes: any[]): any[] {
    const result: any[] = [];
    // Reverse the order of array deletions generated by deep-diff to get the correct result
    // See https://github.com/flitbit/diff/issues/35 and https://github.com/flitbit/diff/issues/47

    function addReversedChanges(changesToReverse: any[]): void {
        changesToReverse.reverse();
        for (let i = 0; i < changesToReverse.length; i++) {
            result.push(changesToReverse[i]);
        }
    }

    let currentArrayDeletionChanges: any[] | null = null;
    for (let i = 0; i < changes.length; i++) {
        const change = changes[i];
        if (change.kind == "A" && change.item.kind == "D") {
            if (currentArrayDeletionChanges) {
                if (pathsEqual(currentArrayDeletionChanges[0].path, change.path)) {
                    currentArrayDeletionChanges.push(change);
                    continue;
                } else {
                    addReversedChanges(currentArrayDeletionChanges);
                    currentArrayDeletionChanges = [change];
                    continue;
                }
            } else {
                currentArrayDeletionChanges = [change];
                continue;
            }
        }

        if (currentArrayDeletionChanges) {
            addReversedChanges(currentArrayDeletionChanges);
            currentArrayDeletionChanges = null;
        }
        result.push(change);
    }

    // In case the last change was itself a delete
    if (currentArrayDeletionChanges) {
        addReversedChanges(currentArrayDeletionChanges);
        currentArrayDeletionChanges = null;
    }

    return result;
}

// Make available globally
(window as any).SavedGames = SavedGames;
(window as any).persistKeyValue = persistKeyValue;
(window as any).pathsEqual = pathsEqual;
(window as any).orderChanges = orderChanges;
